import { useTranslation } from 'react-i18next';
import { useProfiles } from '@/hooks/api/profiles';
import { useBranches } from '@/hooks/api/branches';
import { useMerchantId } from '@/hooks/useMerchantId';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pencil, Loader2, Phone, Mail, Lock, User, Ban, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import type { components } from '@/api/schema';
import { apiErrorMessage } from '@/lib/api-error';

type ProfileRow = components['schemas']['UserResource'] & { full_name: string; role: string };

export default function UsersTab() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { merchantId } = useMerchantId();
  const queryClient = useQueryClient();
  const { data: branches = [] } = useBranches();
  const { data: users = [], isLoading } = useProfiles();

  // Edit state
  const [editUser, setEditUser] = useState<ProfileRow | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editBranch, setEditBranch] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');

  const invalidateProfiles = () => queryClient.invalidateQueries({ queryKey: ['profiles', merchantId] });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: number; role: 'manager' | 'cashier' | 'viewer' }) => {
      const { error } = await apiClient.PATCH('/staff/{user}/role', {
        params: { path: { user: userId } },
        body: { role },
      });
      if (error) throw new Error('message' in error ? String(error.message) : t('cust_common.error'));
    },
  });

  const updateStaffMutation = useMutation({
    mutationFn: async ({ userId, body }: { userId: number; body: Record<string, unknown> }) => {
      const { error } = await apiClient.PATCH('/staff/{user}', {
        params: { path: { user: userId } },
        body,
      });
      if (error) throw new Error('message' in error ? String(error.message) : t('cust_common.error'));
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ userId, action }: { userId: number; action: 'block' | 'unblock' }) => {
      const path = action === 'block' ? '/staff/{user}/block' as const : '/staff/{user}/unblock' as const;
      const { error } = await apiClient.POST(path, { params: { path: { user: userId } } });
      if (error) throw new Error('message' in error ? String(error.message) : t('cust_common.error'));
    },
    onSuccess: (_data, { action }) => {
      invalidateProfiles();
      toast({ title: action === 'block' ? t('cust_ut.blocked_toast') : t('cust_ut.activated_toast') });
    },
    onError: (err: Error) => toast({ title: t('cust_common.error'), description: err.message, variant: 'destructive' }),
  });

  const openEdit = (u: ProfileRow) => {
    setEditUser(u);
    setEditRole(u.role);
    setEditBranch(u.branch_id != null ? String(u.branch_id) : 'all');
    setEditFullName(u.full_name || '');
    setEditPhone(u.phone || '');
    setEditEmail('');
    setEditPassword('');
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    try {
      const branchId = editBranch && editBranch !== 'all' ? Number(editBranch) : null;

      if (editRole !== editUser.role && ['manager', 'cashier', 'viewer'].includes(editRole)) {
        await updateRoleMutation.mutateAsync({ userId: editUser.id, role: editRole as 'manager' | 'cashier' | 'viewer' });
      }

      const staffBody: Record<string, unknown> = {};
      if (branchId !== (editUser.branch_id ?? null)) staffBody.branch_id = branchId;
      if (editFullName && editFullName !== editUser.full_name) staffBody.full_name = editFullName;
      if (editPhone && editPhone !== editUser.phone) staffBody.phone = editPhone;
      if (editEmail.trim()) staffBody.email = editEmail.trim();
      if (editPassword.trim()) {
        if (editPassword.trim().length < 8) {
          toast({ title: t('cust_common.error'), description: t('cust_ut.password_short'), variant: 'destructive' });
          return;
        }
        staffBody.password = editPassword.trim();
        staffBody.password_confirmation = editPassword.trim();
      }
      if (Object.keys(staffBody).length > 0) {
        await updateStaffMutation.mutateAsync({ userId: editUser.id, body: staffBody });
      }

      invalidateProfiles();
      toast({ title: t('cust_ut.saved_toast'), description: t('cust_ut.user_updated', { name: editFullName || editUser.full_name }) });
      setEditUser(null);
    } catch (err) {
      toast({ title: t('cust_common.error'), description: apiErrorMessage(err, t('cust_common.error')), variant: 'destructive' });
    }
  };

  const saving = updateRoleMutation.isPending || updateStaffMutation.isPending;

  const roleLabel: Record<string, string> = {
    owner: t('cust_common.role_owner'), manager: t('cust_common.role_manager'), cashier: t('cust_common.role_cashier'), viewer: t('cust_common.role_viewer'),
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-foreground">{t('cust_ut.list_title')}</h2>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_users.col_name')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_users.col_phone')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_common.role')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_users.col_branch')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_users.col_state')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t('cust_common.loading')}</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t('cust_users.not_found')}</td></tr>
            ) : (
              users.map(u => {
                const branch = branches.find(b => b.id === u.branch_id);
                return (
                  <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="p-3 text-sm text-foreground font-medium">{u.full_name || '—'}</td>
                    <td className="p-3 text-sm text-muted-foreground font-mono">{u.phone || '—'}</td>
                    <td className="p-3"><Badge variant="secondary" className="text-xs capitalize">{roleLabel[u.role] || u.role}</Badge></td>
                    <td className="p-3 text-sm text-muted-foreground">{branch?.name ?? t('cust_common.all')}</td>
                    <td className="p-3">
                      <Badge className={`text-xs border-0 ${u.status === 'active' ? 'bg-success/10 text-success' : u.status === 'pending' ? 'bg-warning/10 text-warning' : 'bg-destructive/10 text-destructive'}`}>
                        {u.status === 'active' ? t('cust_common.status_active') : u.status === 'pending' ? t('cust_common.status_pending') : t('cust_common.status_blocked')}
                      </Badge>
                    </td>
                    <td className="p-3">
                      {u.role !== 'owner' && (
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(u)} className="h-8 w-8" title={t('cust_users.edit')}>
                            <Pencil size={14} />
                          </Button>
                          {u.status !== 'pending' && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => toggleStatusMutation.mutate({ userId: u.id, action: u.status === 'active' ? 'block' : 'unblock' })}
                              disabled={toggleStatusMutation.isPending && toggleStatusMutation.variables?.userId === u.id}
                              className={`h-8 w-8 ${u.status === 'active' ? 'text-destructive hover:text-destructive' : 'text-success hover:text-success'}`}
                              title={u.status === 'active' ? t('cust_users.block') : t('cust_ut.activate')}
                            >
                              {toggleStatusMutation.isPending && toggleStatusMutation.variables?.userId === u.id ? <Loader2 size={14} className="animate-spin" /> : u.status === 'active' ? <Ban size={14} /> : <CheckCircle size={14} />}
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={open => { if (!open) setEditUser(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('cust_users.edit_title')}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <User size={14} className="text-muted-foreground" /> {t('cust_ut.name')}
              </label>
              <Input
                value={editFullName}
                onChange={e => setEditFullName(e.target.value)}
                placeholder={t('cust_users.full_name_ph')}
              />
            </div>

            {/* Phone */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Phone size={14} className="text-muted-foreground" /> {t('cust_users.col_phone')}
              </label>
              <Input
                value={editPhone}
                onChange={e => setEditPhone(e.target.value)}
                placeholder="+998901234567"
              />
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Mail size={14} className="text-muted-foreground" /> {t('cust_ut.email_new')}
              </label>
              <Input
                type="email"
                value={editEmail}
                onChange={e => setEditEmail(e.target.value)}
                placeholder={t('cust_ut.leave_empty')}
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Lock size={14} className="text-muted-foreground" /> {t('cust_ut.new_password')}
              </label>
              <Input
                type="password"
                value={editPassword}
                onChange={e => setEditPassword(e.target.value)}
                placeholder={t('cust_ut.leave_empty')}
              />
              <p className="text-xs text-muted-foreground">{t('cust_ut.min_6')}</p>
            </div>

            {/* Role */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('cust_common.role')}</label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">{t('cust_common.role_manager')}</SelectItem>
                  <SelectItem value="cashier">{t('cust_uappr.role_cashier_seller')}</SelectItem>
                  <SelectItem value="viewer">{t('cust_common.role_viewer')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Branch */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('cust_users.col_branch')}</label>
              <Select value={editBranch || 'all'} onValueChange={setEditBranch}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('cust_common.all_branches')}</SelectItem>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button className="w-full" onClick={handleSaveEdit} disabled={saving}>
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
              {t('cust_users.save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
